import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import Label from 'components/Label';
import Icon from 'components/Icon';
import Dropdown from 'components/Dropdown';

import Styles from './DropdownMulti.module.scss';

export const DropdownMulti = ({ field, setFieldValue = () => {}, name, label, error, items = [] }) => {
  const [emptyDropdownsCount, setEmptyDropdownsCount] = useState(0);

  const actualItems = field?.value && Array.isArray(field.value) ? field.value : items;

  const selectedItems = useMemo(() => actualItems.filter((item) => item.isSelected), [actualItems]);
  const availableItems = useMemo(() => actualItems.filter((item) => !item.isSelected), [actualItems]);

  useEffect(() => {
    if (emptyDropdownsCount > 0 && availableItems.length === 0) {
      setEmptyDropdownsCount(0);
    }
  }, [availableItems.length, emptyDropdownsCount]);

  const dropdownsToShow = useMemo(() => {
    const dropdowns = [...selectedItems];
    for (let i = 0; i < emptyDropdownsCount; i++) {
      dropdowns.push(null);
    }

    if (dropdowns.length === 0) {
      dropdowns.push(null);
    }

    return dropdowns;
  }, [selectedItems, emptyDropdownsCount]);

  const handleChange = (selectedItem, isEmptyDropdown) => (e) => {
    const newSelectedId = parseInt(e.target.value);
    if (!newSelectedId) {
      return;
    }

    const updatedItems = actualItems.map((item) => {
      if (selectedItem && item.id === selectedItem.id) {
        return { ...item, isSelected: false };
      }
      if (item.id === newSelectedId) {
        return { ...item, isSelected: true };
      }
      return item;
    });

    if (isEmptyDropdown && emptyDropdownsCount > 0) {
      setEmptyDropdownsCount(emptyDropdownsCount - 1);
    }

    setFieldValue(field.name, updatedItems, true);
  };

  const handleAddDropdown = () => {
    if (availableItems.length > 0) {
      setEmptyDropdownsCount(emptyDropdownsCount + 1);
    }
  };

  const handleRemoveDropdown = (selectedItem, isEmptyDropdown) => () => {
    if (isEmptyDropdown) {
      if (emptyDropdownsCount > 0) {
        setEmptyDropdownsCount(emptyDropdownsCount - 1);
      }
      return;
    }

    if (!selectedItem) return;

    const updatedItems = actualItems.map((item) => {
      if (item.id === selectedItem.id) {
        return { ...item, isSelected: false };
      }
      return item;
    });

    setFieldValue(field.name, updatedItems, true);
  };

  return (
    <div className={Styles['dropdown-multi-container']}>
      {dropdownsToShow.map((selectedItem, index) => {
        const isEmptyDropdown = selectedItem === null;
        const isFirstDropdown = index === 0;
        const canAddMore = availableItems.length > 0;

        return (
          <div
            key={selectedItem ? `selected-${selectedItem.id}` : `empty-${index}`}
            className={Styles['dropdown-multi-item']}
          >
            {label && (
              <Label name={`${name}-${index}`} error={error}>
                {label}
              </Label>
            )}

            <div className={Styles['dropdown-controls']}>
              <Dropdown
                className={Styles['dropdown-in-multi']}
                field={{ name: field.name, value: selectedItem?.id || '' }}
                name={field.name}
                onChange={handleChange(selectedItem, isEmptyDropdown)}
                error={error}
              >
                {(selectedItem ? [...availableItems, selectedItem] : availableItems).map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </Dropdown>

              {isFirstDropdown ? (
                canAddMore && <Icon icon="plus" size={25} onClick={handleAddDropdown} />
              ) : (
                <>
                  {canAddMore && <Icon icon="plus" size={25} onClick={handleAddDropdown} />}
                  <Icon
                    icon="minus"
                    size={25}
                    className="icon image-container"
                    onClick={handleRemoveDropdown(selectedItem, isEmptyDropdown)}
                  />
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

DropdownMulti.propTypes = {
  field: PropTypes.object,
  setFieldValue: PropTypes.func,
  name: PropTypes.string,
  label: PropTypes.string,
  error: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      isSelected: PropTypes.bool
    })
  )
};

export default DropdownMulti;
